import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { CoinRentalRoom } from "../../../api/API";
import "./RoomCarousel.css";

export interface RoomCarouselProps {
  rooms: CoinRentalRoom[];
  selectedRoom: CoinRentalRoom | null;
  disabled?: boolean;
  onSelect: (room: CoinRentalRoom) => void;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
}

const RoomCarousel: React.FC<RoomCarouselProps> = ({
  rooms,
  selectedRoom,
  disabled = false,
  onSelect
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1
  });

  const updatePagination = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel || carousel.clientWidth <= 0) return;

    const totalPages = Math.max(1, Math.ceil(carousel.scrollWidth / carousel.clientWidth));
    const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    const scrollProgress = maxScroll > 0 ? carousel.scrollLeft / maxScroll : 0;
    const currentPage = Math.min(
      totalPages,
      Math.max(1, Math.round(scrollProgress * (totalPages - 1)) + 1)
    );

    setPagination(current =>
      current.currentPage === currentPage && current.totalPages === totalPages
        ? current
        : { currentPage, totalPages }
    );
  }, []);

  useLayoutEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    updatePagination();
    window.addEventListener("resize", updatePagination);
    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(updatePagination);
    resizeObserver?.observe(carousel);

    return () => {
      window.removeEventListener("resize", updatePagination);
      resizeObserver?.disconnect();
    };
  }, [rooms.length, updatePagination]);

  return (
    <>
      <div
        ref={carouselRef}
        className="room-carousel"
        role="group"
        aria-label="Available rooms"
        onScroll={updatePagination}
      >
        {rooms.map(room => {
          const isSelected = selectedRoom?.id === room.id;

          return (
            <button
              type="button"
              key={room.id}
              className={`room-carousel__card${isSelected ? " room-carousel__card--selected" : ""}`}
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => onSelect(room)}
            >
              {room.imageUrl ? (
                <img
                  className="room-carousel__image"
                  src={room.imageUrl}
                  alt=""
                />
              ) : (
                <span className="room-carousel__image room-carousel__image--placeholder" aria-hidden="true" />
              )}
              <span className="room-carousel__body">
                <span className="room-carousel__name">{room.name}</span>
                <span className="room-carousel__meta">
                  {room.size ? `${room.size} m² · ` : ""}
                  {room.venueName}
                </span>
                <span className="room-carousel__price">
                  From Ȼ{room.minCoins30min} / 30 min
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {pagination.totalPages > 1 && (
        <div
          className="room-carousel__pagination"
          role="status"
          aria-live="polite"
          aria-label={`Room page ${pagination.currentPage} of ${pagination.totalPages}`}
        >
          <span className="room-carousel__dots" aria-hidden="true">
            {Array.from({ length: pagination.totalPages }, (_, index) => (
              <span
                key={index}
                className={`room-carousel__dot${index + 1 === pagination.currentPage ? " room-carousel__dot--active" : ""}`}
              />
            ))}
          </span>
          <span>{pagination.currentPage} of {pagination.totalPages}</span>
        </div>
      )}
    </>
  );
};

export default RoomCarousel;
